import {
  getV1CompaniesCompanyIdDriverDaysDriverDayIdResourceUsageLogs,
  getV1CompaniesCompanyIdJobsJobIdResourceUsageLogs,
  patchV1CompaniesCompanyIdDriverDaysDriverDayIdResourceUsageLogs,
  patchV1CompaniesCompanyIdJobsJobIdResourceUsageLogs,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { ResourceUsageLogDTO } from '~pages/Approvals/DriverPay/helpers';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ResourceUsageLog } from './models';

const useResourceUsageLogs = () => {
  const { addOnsStore, userStore } = useStores();

  const getResourceUsageLogsByDriverDay = async (driverDayId: string) => {
    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const resourceUsageLogs = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdDriverDaysDriverDayIdResourceUsageLogs({
            path: { 'company-id': companyId, 'driver-day-id': driverDayId },
            query,
          }),
        ResourceUsageLog.parse,
      );
      addOnsStore.setResourceUsageLogsByDriverDayId(driverDayId, resourceUsageLogs);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.add_ons.falied_to_fetch_resource_usage_logs'),
      );
    }
  };

  const getResourceUsageLogsByJob = async (companyId: string, jobId: string) => {
    try {
      const resourceUsageLogs = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdJobsJobIdResourceUsageLogs({
            path: { 'company-id': companyId, 'job-id': jobId },
            query,
          }),
        ResourceUsageLog.parse,
      );
      addOnsStore.setResourceUsageLogsByJobId(jobId, resourceUsageLogs);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.add_ons.falied_to_fetch_resource_usage_logs'),
      );
    }
  };

  const batchUpdateResourceUsageLogsByDriverDay = async (
    driverDayId: string,
    logs: ResourceUsageLogDTO[],
    dateOfUse: string,
  ) => {
    let updatedResourceUsageLogs: ResourceUsageLog[] = [];
    let shouldUpdateStore = false;

    try {
      const companyId = getEffectiveUserCompanyId(userStore);
      const response =
        await patchV1CompaniesCompanyIdDriverDaysDriverDayIdResourceUsageLogs({
          path: { 'company-id': companyId, 'driver-day-id': driverDayId },
          body: {
            resource_usage_logs: logs.map((log) => {
              return ResourceUsageLog.deparse(log, dateOfUse);
            }),
          },
        });

      updatedResourceUsageLogs = response.data.data.map((log) => {
        return ResourceUsageLog.parse(log);
      });

      shouldUpdateStore = true;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_update'));
    } finally {
      if (shouldUpdateStore) {
        addOnsStore.setResourceUsageLogsByDriverDayId(
          driverDayId,
          updatedResourceUsageLogs,
        );
      }
    }
  };

  const batchUpdateResourceUsageLogsByJob = async (
    companyId: string,
    jobId: string,
    logs: ResourceUsageLogDTO[],
    dateOfUse: string,
  ) => {
    let updatedResourceUsageLogs: ResourceUsageLog[] = [];
    let shouldUpdateStore = false;

    try {
      const response = await patchV1CompaniesCompanyIdJobsJobIdResourceUsageLogs({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: {
          resource_usage_logs: logs.map((log) => {
            return ResourceUsageLog.deparse(log, dateOfUse);
          }),
        },
      });

      updatedResourceUsageLogs = response.data.data.map((log) => {
        return ResourceUsageLog.parse(log);
      });

      shouldUpdateStore = true;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_update'));
    } finally {
      if (shouldUpdateStore) {
        addOnsStore.setResourceUsageLogsByJobId(jobId, updatedResourceUsageLogs);
      }
    }
  };
  return {
    batchUpdateResourceUsageLogsByDriverDay,
    batchUpdateResourceUsageLogsByJob,
    getResourceUsageLogsByDriverDay,
    getResourceUsageLogsByJob,
  };
};

export default useResourceUsageLogs;
